import type { StatusVariants } from "@/components/molecules";
import type { PaginatedResponse } from "@/types/api";

export enum PayoutStatus {
  APPROVED = "APPROVED",
  PENDING = "PENDING",
  DECLINED = "DECLINED",
}

export const statusMapFunding: Record<string, PayoutStatus> = {
  Approved: PayoutStatus.APPROVED,
  Pending: PayoutStatus.PENDING,
  Declined: PayoutStatus.DECLINED,
};

/**
 * ISO currency code of a funding request, e.g. "SAR" or "JOD".
 *
 * Deliberately a plain string rather than a union. The value comes from the
 * funded group, and the set of currencies we support is the backend's to decide
 * - the options endpoint is the source of truth. A narrow union here used to
 * exclude JOD, which made every Jordanian funding row a type that lied.
 */
export type CurrencyCode = string;

export enum DistributionMethod {
  NO_LIMITS = "NO_LIMITS",
  EQUAL_PARTS = "EQUAL_PARTS",
  MANUAL = "MANUAL",
}

export interface Approval {
  id: string;
  amount: number;
  startDate: string | null;
  contractStartDate?: string | null;
  endDate?: string | null;
  distributions: Distribution[];
  cashToCreditConversionRate?: string | null;
}

export interface Distribution {
  brandId: string;
  percent: number;
}

export interface IPayout {
  id: string;
  bankTransferId: string | null;
  referenceNumber: number;
  createdAt: string;
  status: PayoutStatus;
  requestedAmount: number;
  receivedAmount: number;
  currency: CurrencyCode;
  brand?: string;
  isTransferredFromBank?: boolean;
}

export interface PayoutGroup {
  groupId: string;
  requestedAmount: number;
  cashGranted: number;
  cashToCreditConversionRate?: number;
  contractStartDate?: string;
  contractDurationMonths?: number;
  currency: CurrencyCode;
  bankTransferId?: string;
  distributionMethod: DistributionMethod;
  distributions: Distribution[];
}

export const FUNDING_APPLICATION_STATUS_MAP: StatusVariants = {
  Approved: { badge: "success", dot: "#65A30D" },
  Pending: { badge: "info", dot: "#D97706" },
  Declined: { badge: "warning", dot: "#DC2626" },
};

export interface IFundingApplications {
  id: string;
  referenceNumber: string;
  createdAt: string;
  status: PayoutStatus;
  requestedAmount: number;
  receivedAmount: number | null;
  /** Currency the amounts above are in. Comes from the funded group. */
  currency: CurrencyCode;
  isTransferredFromBank: boolean;
  bankTransferId?: string;
  transferredAt?: string | null;
}

export enum ConsumptionStatus {
  ACTIVE = "ACTIVE",
  UPCOMING = "UPCOMING",
  DEPLETED = "DEPLETED",
}

export const cunsumptionStatusMap: Record<string, ConsumptionStatus> = {
  Active: ConsumptionStatus.ACTIVE,
  Upcoming: ConsumptionStatus.UPCOMING,
  Depleted: ConsumptionStatus.DEPLETED,
};

export interface IConsumption {
  id: string;
  referenceNumber: string;
  startDate: string;
  amount: number;
  consumedAmount: number;
  remainingAmount: number;
  status: ConsumptionStatus;
}

export interface FundingConsumptionTotals {
  totalAmount: number;
  totalConsumedAmount: number;
  totalRemainingAmount: number;
}

export type FundingConsumptionResponse = PaginatedResponse<IConsumption> &
  FundingConsumptionTotals;

export const FUNDING_CONSUMPTION_STATUS_MAP: StatusVariants = {
  Active: { badge: "success", dot: "#65A30D" },
  Upcoming: { badge: "blue", dot: "#C8D1F6" },
  Depleted: { badge: "default", dot: "#111827" },
};

export interface IUpdateFundingCreditPeriodRequest {
  startDate?: string | null;
  endDate?: string | null;
  timezoneOffsetMinutes: number;
}

export interface IUpdateFundingTermsRequest {
  requestedAmount: number;
  cashGranted: number;
  cashToCreditConversionRate?: number | null;
  contractStartDate?: string | null;
  contractDurationMonths?: number | null;
}

export interface IUpdateTransferRequest {
  approvedAmount?: number;
  receivedAmount?: number;
  startDate?: string;
  contractStartDate?: string;
  dateOfBankTransfer?: string | null;
  bankTransferId?: string | null;
  timezoneOffsetMinutes: number;
  isTransferredFromBank?: boolean;
}

export interface IApplicationInfo {
  id: string;
  referenceNumber: string;
  groupId: string;
  groupName: string;
  bankTransferId: string | null;
  dateOfBankTransfer: string | null;
  isTransferredFromBank: boolean;
  createdAt: string;
  updatedAt: string;
  status: PayoutStatus;
  requestedAmount: number;
  cashGranted?: number | null;
  receivedAmount: number;
  currency: string;
  distributionMethod: string;
  approval?: Approval;
  startDate?: string | null;
  contractStartDate?: string | null;
  contractDurationMonths?: number | null;
  transferredAt?: string | null;
  cashToCreditConversionRate?: string | null;
}

export interface FundingBrandStat {
  startingBalance: number;
  upcomingBalance: number;
  consumedBalance: number;
  ramainingBalance: number;
  totalGranted: number;
  /** Currency of the group the brand belongs to. */
  currency: string;
  charts: {
    date: string;
    actual: number;
  }[];
}
